import { useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSnackbars, type DataTableColumnsSettingsConfig } from '@customerio/pluma-components/react';
import { ATTRIBUTE_COLUMN_PREFIX, attributeColumnId } from '@/features/people';
import { useAuth } from '@/services/auth';
import { useAttributeCatalog, useAttributeNameSearch } from '@/services/db/attribute/queries';
import { getWorkspaceSettings, workspaceSettingsKeys } from '@/services/db/environment/queries';
import { useUpdateUserEnvSettings } from '@/services/db/user-env-settings/mutations';
import { useUserEnvSettingsOptional } from '@/services/db/user-env-settings/queries';
import { useSettingsAccountId } from '@/services/db/user-env-settings/use-settings-account';
import { isEmptyString } from '@/utils/string';

// Always present as fixed columns, so never offered as attribute columns.
const HIDDEN_ATTRIBUTES = new Set(['created_at', 'email']);

/**
 * The workspace-level default visible set, used until the user saves their
 * own — the workspace's `customer_columns` setting can override it.
 */
const DEFAULT_ENV_COLUMNS: readonly string[] = ['id'];

/** The two persisted column sources: the user's saved set and the workspace-level fallback. */
export function usePersistedColumnSources({
  workspaceId,
  isSampleAccess,
  isSampleWorkspace,
}: {
  workspaceId: number;
  isSampleAccess: boolean;
  isSampleWorkspace: boolean;
}) {
  const { user } = useAuth();
  const settingsAccountId = useSettingsAccountId();

  const settingsQuery = useUserEnvSettingsOptional({
    accountId: settingsAccountId,
    userId: user?.id,
    workspaceId,
    enabled: !isSampleAccess,
  });

  const applyWorkspaceColumns = !isSampleAccess && !isSampleWorkspace;
  const workspaceSettingsQuery = useQuery({
    queryKey: workspaceSettingsKeys.detail(workspaceId),
    queryFn: ({ signal }) => getWorkspaceSettings(workspaceId, signal),
    enabled: applyWorkspaceColumns,
    staleTime: 300_000,
  });

  return {
    // A skipped or failed read is settled; only a first load in flight isn't.
    isSettled: !settingsQuery.isLoading && !workspaceSettingsQuery.isLoading,
    settingsColumns: settingsQuery.data?.people_page.columns ?? [],
    workspaceColumns: applyWorkspaceColumns
      ? (workspaceSettingsQuery.data?.settings?.customer_columns ?? DEFAULT_ENV_COLUMNS)
      : DEFAULT_ENV_COLUMNS,
  };
}

/**
 * The persisted visible set — the workspace-level set applies only until the
 * user saves their own. Null until both reads have settled: an unread setting
 * reads as an empty one, so resolving early would show the workspace set and
 * then swap it for the user's as the other read lands.
 */
export function resolvePersistedColumns({
  isSettled,
  settingsColumns,
  workspaceColumns,
}: {
  isSettled: boolean;
  settingsColumns: readonly string[];
  workspaceColumns: readonly string[];
}): readonly string[] | null {
  if (!isSettled) return null;
  if (settingsColumns.length === 0 && workspaceColumns.length !== 0) return workspaceColumns;
  return settingsColumns;
}

/**
 * The visible column set: local choice → the user's saved `people_page.columns`
 * → the workspace's `customer_columns` → `id`, and null until the persisted
 * sources have settled. Saves are optimistic — the local choice applies
 * immediately and a failed save rolls back to the last persisted set with an
 * error snackbar.
 */
function useSelectedColumns({ workspaceId, isSampleWorkspace }: { workspaceId: number; isSampleWorkspace: boolean }) {
  const { account, user } = useAuth();
  const snackbars = useSnackbars();
  const isSampleAccess = account?.is_sample_account_access === true;
  const settingsAccountId = useSettingsAccountId();

  const [localColumns, setLocalColumns] = useState<readonly string[] | null>(null);
  const latestColumnsRef = useRef<readonly string[] | null>(null);

  const { isSettled, settingsColumns, workspaceColumns } = usePersistedColumnSources({
    workspaceId,
    isSampleAccess,
    isSampleWorkspace,
  });

  const updateSettings = useUpdateUserEnvSettings({
    scope: user != null ? { id: `user-env-settings:${settingsAccountId}:${user.id}:${workspaceId}` } : undefined,
    onError: (_error, variables) => {
      // Only the latest save matters: a stale save failing after a newer one
      // was queued must not roll back the newer choice.
      if (variables.settings.people_page?.columns !== latestColumnsRef.current) return;
      setLocalColumns(null);
      latestColumnsRef.current = null;
      snackbars.create({
        variant: 'critical',
        id: 'people.columns',
        message: "Column settings couldn't be saved. Try again.",
      });
    },
  });

  const selectedColumns = localColumns ?? resolvePersistedColumns({ isSettled, settingsColumns, workspaceColumns });

  const saveColumns = (columns: string[]) => {
    setLocalColumns(columns);
    latestColumnsRef.current = columns;
    // Sample-account access never persists — the local choice still applies.
    if (user == null || settingsAccountId == null || isSampleAccess) return;
    updateSettings.mutate({
      accountId: settingsAccountId,
      userId: user.id,
      workspaceId,
      settings: { people_page: { columns } },
    });
  };

  return { selectedColumns, saveColumns };
}

/**
 * The columns-menu catalog: the server-paged attribute list (first page loads
 * with the page, later pages on menu scroll) plus server-side search.
 * Searched names accumulate as column definitions, so a column toggled on
 * from search results keeps existing after the search clears.
 */
function useAttributeCatalogMenu(workspaceId: number) {
  const catalog = useAttributeCatalog(workspaceId);
  const catalogAttributes = useMemo(() => catalog.data?.pages.flat() ?? [], [catalog.data]);

  const [searchTerm, setSearchTerm] = useState('');
  const searchQuery = useAttributeNameSearch(workspaceId, searchTerm);

  const [searchedAttributes, setSearchedAttributes] = useState<readonly string[]>([]);
  const searchMatches = searchQuery.data;
  if (searchMatches != null) {
    const merged = [...new Set([...searchedAttributes, ...searchMatches])];
    if (merged.length !== searchedAttributes.length) {
      setSearchedAttributes(merged);
    }
  }

  const searchResultColumnIds = useMemo(() => {
    if (isEmptyString(searchTerm) || searchMatches == null) return null;
    return searchMatches.map(attributeColumnId);
  }, [searchTerm, searchMatches]);

  const columnsSettings: DataTableColumnsSettingsConfig = {
    withSearch: true,
    onLoadMore: () => {
      if (catalog.isFetching || catalog.hasNextPage === false) return;
      void catalog.fetchNextPage();
    },
    shouldLoadMore: catalog.hasNextPage && searchResultColumnIds == null,
    isLoadingMore: catalog.isFetching,
    onSearch: setSearchTerm,
    searchResultColumnIds,
    isSearching: searchQuery.isFetching,
  };

  return { catalogAttributes, searchedAttributes, columnsSettings };
}

type UsePeopleColumnsOptions = {
  workspaceId: number;
  /** The environment's bounded sent-attributes list — the catalog's seed. */
  sentAttributes: readonly string[];
  /** True while `sentAttributes` is still being read — an unread list is not an empty one. */
  areSentAttributesLoading: boolean;
  /** Sample workspaces keep the default column set (their settings aren't real). */
  isSampleWorkspace: boolean;
};

/**
 * Attribute-column state for the People list: the visible set (persisted per
 * user to the `people_page.columns` setting) and the columns-menu catalog —
 * sent attributes plus the server-paged attribute list, with server-side
 * search.
 *
 * `isReady` is false until every column input has landed. The table fixes its
 * column order at first paint, so a caller that renders it earlier gets the
 * attribute columns appended after the fixed `created_at` one, and keeps that
 * order for the rest of the session.
 */
export function usePeopleColumns({
  workspaceId,
  sentAttributes,
  areSentAttributesLoading,
  isSampleWorkspace,
}: UsePeopleColumnsOptions) {
  const { selectedColumns, saveColumns } = useSelectedColumns({ workspaceId, isSampleWorkspace });
  const { catalogAttributes, searchedAttributes, columnsSettings } = useAttributeCatalogMenu(workspaceId);

  const allAttributes = useMemo(
    () =>
      [...new Set([...sentAttributes, ...catalogAttributes, ...searchedAttributes])].filter(
        (attribute) => !HIDDEN_ATTRIBUTES.has(attribute),
      ),
    [sentAttributes, catalogAttributes, searchedAttributes],
  );

  const columnVisibility = useMemo(() => {
    const selected = new Set(selectedColumns ?? []);
    const visibility: Record<string, boolean> = {};
    for (const attribute of allAttributes) {
      visibility[attributeColumnId(attribute)] = selected.has(attribute);
    }
    return visibility;
  }, [allAttributes, selectedColumns]);

  const onColumnVisibilityChange = (visibility: Record<string, boolean>) => {
    saveColumns(
      Object.entries(visibility)
        .filter(([columnId, visible]) => visible && columnId.startsWith(ATTRIBUTE_COLUMN_PREFIX))
        .map(([columnId]) => columnId.slice(ATTRIBUTE_COLUMN_PREFIX.length)),
    );
  };

  return {
    isReady: selectedColumns != null && !areSentAttributesLoading,
    allAttributes,
    columnVisibility,
    onColumnVisibilityChange,
    columnsSettings,
  };
}
